import { useId, useLayoutEffect, useRef, type ReactNode, type RefObject } from "react";
import { Link, useSearchParams } from "react-router";
import { Icon } from "./icons";
import { HelpTooltip } from "./HelpTooltip";

/** One surface for an object's tabs and working area. Feature owners keep their API/actions. */
export function WorkspaceFrame({ header, tabs, toolbar, children, className = "", purpose }: {
  header?: ReactNode; tabs?: ReactNode; toolbar?: ReactNode; children: ReactNode; className?: string; purpose?: "training" | "frozen_test" | "evaluation" | "neutral";
}) {
  return <section className={`workspace-frame ${className}`} data-card-purpose={purpose}>
    {header && <div className="workspace-frame-header">{header}</div>}
    {tabs && <div className="workspace-frame-navigation">{tabs}</div>}
    {toolbar && <div className="workspace-frame-toolbar">{toolbar}</div>}
    <div className="workspace-frame-content">{children}</div>
  </section>;
}

/** Reveal a current section without moving document scroll or keyboard focus. */
export function useRevealCurrentSection(nav: RefObject<HTMLElement | null>, current: string, count: number) {
  useLayoutEffect(() => {
    const element = nav.current;
    const selected = element?.querySelector<HTMLElement>('[aria-current="page"], [aria-selected="true"]');
    if (!element || !selected) return;
    // Reveal a deep-linked section inside its own strip; never scroll the document or move focus.
    const reveal = () => {
      const bounds = element.getBoundingClientRect();
      const item = selected.getBoundingClientRect();
      const delta = item.left < bounds.left ? item.left - bounds.left : item.right > bounds.right ? item.right - bounds.right : 0;
      if (delta) element.scrollBy({ left: delta, behavior: "instant" });
    };
    reveal();
    const observer = new ResizeObserver(reveal);
    observer.observe(element);
    // A loaded count can widen an earlier tab without resizing the selected tab itself.
    for (const child of element.children) observer.observe(child);
    return () => observer.disconnect();
  }, [nav, current, count]);
}

/** URL-backed section navigation preserves the owning page's filters/context on Back and refresh. */
export function WorkspaceTabs({ current, tabs, label = "Workspace sections", className = "", testId }: {
  current: string; tabs: [string, ReactNode][]; label?: string; className?: string; testId?: string;
}) {
  const [params] = useSearchParams();
  const nav = useRef<HTMLElement>(null);
  useRevealCurrentSection(nav, current, tabs.length);
  return <nav ref={nav} className={`workspace-tabs ${className}`} aria-label={label} data-testid={testId}>
    {tabs.map(([key, title]) => {
      const next = new URLSearchParams(params);
      next.set("tab", key);
      return <Link key={key} to={`?${next}`} aria-current={current === key ? "page" : undefined}>{title}</Link>;
    })}
  </nav>;
}

/** The feature owns filters, selection and actions. This layer only lays them out. */
export function WorkspaceToolbar({ label, children, actions }: { label: string; children: ReactNode; actions?: ReactNode }) {
  return <section aria-label={label} className="workspace-toolbar">
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{children}</div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </section>;
}

export function Workspace({ children, inspector }: { children: ReactNode; inspector?: ReactNode }) {
  return <div className="workspace-layout"><div className={`workspace-grid ${inspector ? "has-inspector" : ""}`}>
    <div className="workspace-parent min-w-0 space-y-4">{children}</div>{inspector}
  </div></div>;
}

export function Inspector({ title, subtitle, onClose, children, footer }: {
  title: ReactNode; subtitle?: ReactNode; onClose?: () => void; children: ReactNode; footer?: ReactNode;
}) {
  const titleId = useId();
  return <aside className="workspace-inspector" aria-labelledby={titleId}>
    <div className="flex items-start justify-between gap-3 border-b border-hair p-4">
      <div className="min-w-0"><h2 id={titleId} className="break-words text-base">{title}</h2>{subtitle && <div className="mt-1 text-xs text-muted">{subtitle}</div>}</div>
      {onClose && <button type="button" className="icon-button" onClick={onClose} aria-label="Close inspector"><Icon name="close" size={16} /></button>}
    </div>
    <div className="min-w-0 space-y-5 p-4">{children}</div>
    {footer && <div className="mt-auto space-y-2 border-t border-hair p-4">{footer}</div>}
  </aside>;
}

/** `bare` drops the panel chrome and title for use inside a disclosure, which already names its contents. */
export function EvidencePanel({ title, description, help, actions, children, bare = false }: {
  title: ReactNode; description?: ReactNode; help?: string; actions?: ReactNode; children: ReactNode; bare?: boolean;
}) {
  if (bare) return <div className="grid min-w-0 gap-3" role="group" aria-label={typeof title === "string" ? title : undefined}>
    {(description || actions) && <div className="flex flex-wrap items-center justify-between gap-3">
      {description ? <p className="min-w-0 text-xs text-muted">{description}</p> : <span />}
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>}
    <div className="min-w-0 overflow-x-auto">{children}</div>
  </div>;
  return <section className="evidence-panel">
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hair px-4 py-3">
      <div className="min-w-0"><h2>{title}{help && <HelpTooltip label={typeof title === "string" ? title : "this panel"}>{help}</HelpTooltip>}</h2>{description && <p className="mt-1 text-xs text-muted">{description}</p>}</div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
    <div className="min-w-0 overflow-x-auto">{children}</div>
  </section>;
}
